
import { supabase } from './client'
import {
  RelatedAppRecord,
  Task,
  TaskAttachment,
  TaskNote,
  TaskTimeEntry,
  TaskPrerequisite,
  TaskRow,
  TaskStatusDefaultDays,
  TaskQuote,
} from '../types/database.types'
import { getTaskAttachmentSignedUrl } from './storage'




export type TaskNoteWithAuthor = TaskNote & {
  author_name: string
}

export type TaskAttachmentWithUploader = TaskAttachment & {
  uploader_name: string
}

export type TaskTimeEntryWithAuthor = TaskTimeEntry & {
  author_name: string
}

type TaskRowWithRelation = TaskRow & {
  related_apps: RelatedAppRecord | null
}

const hydrateTask = (task: TaskRowWithRelation): Task => ({
  ...task,
  related_app: task.related_apps?.name || 'Unknown App',
  related_app_record: task.related_apps || null,
})

export const queries = {
  // Get all tasks (with optional completed filter)
  getTasks: async (excludeCompleted = false): Promise<Task[]> => {
    let query = supabase
      .from('tasks')
      .select('*, related_apps(*)')
      .order('created_at', { ascending: false })

    if (excludeCompleted) {
      query = query.neq('status', 'completed')
    }

    const { data, error } = await query

    if (error) {
      console.error('Error fetching tasks:', error)
      throw error
    }

    return ((data as TaskRowWithRelation[] | null) || []).map(hydrateTask)
  },

  getTaskByUniqueId: async (uniqueTaskId: string): Promise<Task | null> => {
    const { data, error } = await supabase
      .from('tasks')
      .select('*, related_apps(*)')
      .eq('unique_task_id', uniqueTaskId)
      .maybeSingle()

    if (error) {
      console.error('Error fetching task by unique ID:', error)
      throw error
    }

    return data ? hydrateTask(data as TaskRowWithRelation) : null
  },

  // Get tasks for current user
  getUserTasks: async (userId: string, excludeCompleted = false): Promise<Task[]> => {
    let query = supabase
      .from('tasks')
      .select('*, related_apps(*)')
      .eq('assigned_to', userId)
      .order('created_at', { ascending: false })

    if (excludeCompleted) {
      query = query.neq('status', 'completed')
    }

    const { data, error } = await query

    if (error) {
      console.error('Error fetching user tasks:', error)
      throw error
    }

    return ((data as TaskRowWithRelation[] | null) || []).map(hydrateTask)
  },

  // Get tasks by status (for charts)
  getTasksByStatus: async (): Promise<Array<{ status: string; count: number }>> => {
    const { data, error } = await supabase.from('tasks').select('status')

    if (error) {
      console.error('Error fetching tasks by status:', error)
      throw error
    }

    // Group by status
    const grouped = (data || []).reduce(
      (acc, task) => {
        const key = task.status || 'unknown'
        const existing = acc.find((item) => item.status === key)
        if (existing) {
          existing.count += 1
        } else {
          acc.push({ status: key, count: 1 })
        }
        return acc
      },
      [] as Array<{ status: string; count: number }>
    )

    return grouped
  },

  // Get tasks by assignee (for charts)
  getTasksByAssignee: async (): Promise<
    Array<{ assigned_to: string | null; count: number; full_name?: string }>
  > => {
    const [{ data: tasks, error: tasksError }, { data: profiles, error: profilesError }] =
      await Promise.all([
        supabase.from('tasks').select('assigned_to, status'),
        supabase.from('profiles').select('id, full_name, email'),
      ])

    if (tasksError) {
      console.error('Error fetching tasks by assignee:', tasksError)
      throw tasksError
    }

    if (profilesError) {
      console.error('Error fetching profiles for assignee chart:', profilesError)
      throw profilesError
    }

    const profileMap = new Map(
      (profiles || []).map((profile) => [profile.id, profile.full_name || profile.email || 'Unknown User'])
    )

    // Group by assignee
    const grouped = (tasks || []).reduce(
      (acc, task) => {
        const key = task.assigned_to || 'unassigned'
        const existing = acc.find((item) => item.assigned_to === key)
        if (existing) {
          existing.count += 1
        } else {
          acc.push({
            assigned_to: key,
            count: 1,
            full_name: key === 'unassigned' ? 'Unassigned' : profileMap.get(key) || 'Unknown User',
          })
        }
        return acc
      },
      [] as Array<{ assigned_to: string | null; count: number; full_name?: string }>
    )

    return grouped
  },

  // Get tasks by related app (for charts)
  getTasksByApp: async (): Promise<Array<{ related_app: string; count: number }>> => {
    const tasks = await queries.getTasks(false)

    const grouped = tasks.reduce(
      (acc, task) => {
        const key = task.related_app || 'unknown'
        const existing = acc.find((item) => item.related_app === key)
        if (existing) {
          existing.count += 1
        } else {
          acc.push({ related_app: key, count: 1 })
        }
        return acc
      },
      [] as Array<{ related_app: string; count: number }>
    )

    return grouped
  },

  // Get tasks by age bucket (for charts)
  getTasksByAgeBucket: async (): Promise<Array<{ bucket: string; count: number }>> => {
    const now = new Date()
    const msInDay = 24 * 60 * 60 * 1000
    const buckets = [
      { label: '< 1 week', test: (days: number) => days < 7 },
      { label: '< 1 month', test: (days: number) => days >= 7 && days < 30 },
      { label: '1-3 months', test: (days: number) => days >= 30 && days < 90 },
      { label: '3-6 months', test: (days: number) => days >= 90 && days < 180 },
      { label: '> 6 months', test: (days: number) => days >= 180 },
    ]

    // Only include non-completed (active) tasks
    const tasks = await queries.getTasks(true)
    const counts: Record<string, number> = {}
    for (const bucket of buckets) {
      counts[bucket.label] = 0
    }
    for (const task of tasks) {
      if (!task.created_at) continue
      const created = new Date(task.created_at)
      const days = Math.floor((now.getTime() - created.getTime()) / msInDay)
      const found = buckets.find((b) => b.test(days))
      if (found) {
        counts[found.label]++
      }
    }
    // Sort buckets in display order
    const order = ['< 1 week', '< 1 month', '1-3 months', '3-6 months', '> 6 months']
    return order.map((label) => ({ bucket: label, count: counts[label] }))
  },

  getRelatedApps: async (): Promise<RelatedAppRecord[]> => {
    const { data, error } = await supabase.from('related_apps').select('*').order('name')

    if (error) {
      console.error('Error fetching related apps:', error)
      throw error
    }

    return data || []
  },

  getTaskStatusDefaultDays: async (): Promise<TaskStatusDefaultDays[]> => {
    const { data, error } = await supabase
      .from('task_status_default_days')
      .select('*')
      .order('status')

    if (error) {
      console.error('Error fetching task status default days:', error)
      throw error
    }

    return data || []
  },

  // Get single task
  getTask: async (taskId: string): Promise<Task | null> => {
    const { data, error } = await supabase
      .from('tasks')
      .select('*, related_apps(*)')
      .eq('id', taskId)
      .single()

    if (error) {
      console.error('Error fetching task:', error)
      throw error
    }

    return hydrateTask(data as TaskRowWithRelation)
  },

  // Get user profile
  getUserProfile: async (userId: string) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single()

    if (error) {
      console.error('Error fetching user profile:', error)
      throw error
    }

    return data
  },

  // Get all profiles (for assignee dropdown)
  getAllProfiles: async () => {
    const { data, error } = await supabase.from('profiles').select('*').order('full_name')

    if (error) {
      console.error('Error fetching profiles:', error)
      throw error
    }

    return data || []
  },

  getTasksPendingQuote: async (userId: string): Promise<Task[]> => {
    const { data, error } = await supabase
      .from('tasks')
      .select('*, related_apps(*)')
      .eq('status', 'pending_quote')
      .eq('assigned_to', userId)
      .order('created_at', { ascending: false })
    if (error) throw error
    return ((data as TaskRowWithRelation[] | null) || []).map(hydrateTask)
  },

  getTaskQuotesByStatus: async (status: TaskQuote['status']): Promise<Array<{ task: Task; quote: TaskQuote }>> => {
    const [{ data: quotes, error: quotesError }, { data: tasks, error: tasksError }] = await Promise.all([
      supabase.from('task_quotes').select('*').eq('status', status).order('created_at', { ascending: false }),
      supabase.from('tasks').select('*, related_apps(*)'),
    ])
    if (quotesError) throw quotesError
    if (tasksError) throw tasksError
    const taskMap = new Map(((tasks as TaskRowWithRelation[] | null) || []).map((task) => [task.id, hydrateTask(task)]))
    return ((quotes as TaskQuote[] | null) || [])
      .map((quote) => ({ task: taskMap.get(quote.task_id), quote }))
      .filter((item): item is { task: Task; quote: TaskQuote } => Boolean(item.task))
  },

  getTaskNotes: async (taskId: string): Promise<TaskNoteWithAuthor[]> => {
    const [{ data: notes, error: notesError }, { data: profiles, error: profilesError }] = await Promise.all([
      supabase.from('task_notes').select('*').eq('task_id', taskId).order('created_at', { ascending: false }),
      supabase.from('profiles').select('id, full_name, email'),
    ])

    if (notesError) {
      console.error('Error fetching task notes:', notesError)
      throw notesError
    }

    if (profilesError) {
      console.error('Error fetching profiles for task notes:', profilesError)
      throw profilesError
    }

    const profileMap = new Map(
      (profiles || []).map((profile) => [profile.id, profile.full_name || profile.email || 'Unknown User'])
    )

    return (notes || []).map((note) => ({
      ...note,
      author_name: profileMap.get(note.created_by) || 'Unknown User',
    }))
  },

  getTaskTimeEntries: async (taskId: string): Promise<TaskTimeEntryWithAuthor[]> => {
    const [{ data: timeEntries, error: timeEntriesError }, { data: profiles, error: profilesError }] = await Promise.all([
      supabase.from('task_time_entries').select('*').eq('task_id', taskId).order('created_at', { ascending: false }),
      supabase.from('profiles').select('id, full_name, email'),
    ])

    if (timeEntriesError) {
      console.error('Error fetching task time entries:', timeEntriesError)
      throw timeEntriesError
    }

    if (profilesError) {
      console.error('Error fetching profiles for task time entries:', profilesError)
      throw profilesError
    }

    const profileMap = new Map(
      (profiles || []).map((profile) => [profile.id, profile.full_name || profile.email || 'Unknown User'])
    )

    return (timeEntries || []).map((entry) => ({
      ...entry,
      author_name: profileMap.get(entry.created_by) || 'Unknown User',
    }))
  },

  getTaskAttachments: async (taskId: string): Promise<TaskAttachmentWithUploader[]> => {
    const [{ data: attachments, error: attachmentsError }, { data: profiles, error: profilesError }] = await Promise.all([
      supabase.from('task_attachments').select('*').eq('task_id', taskId).order('created_at', { ascending: false }),
      supabase.from('profiles').select('id, full_name, email'),
    ])

    if (attachmentsError) {
      console.error('Error fetching task attachments:', attachmentsError)
      throw attachmentsError
    }

    if (profilesError) {
      console.error('Error fetching profiles for task attachments:', profilesError)
      throw profilesError
    }

    const profileMap = new Map(
      (profiles || []).map((profile) => [profile.id, profile.full_name || profile.email || 'Unknown User'])
    )

    return (attachments || []).map((attachment) => ({
      ...attachment,
      uploader_name: profileMap.get(attachment.uploaded_by) || 'Unknown User',
    }))
  },

  getTaskAttachmentAccessUrl: async (attachment: TaskAttachment): Promise<string> => {
    return getTaskAttachmentSignedUrl(attachment.storage_path)
  },

  getTaskPrerequisites: async (): Promise<TaskPrerequisite[]> => {
    const { data, error } = await supabase
      .from('task_prerequisites')
      .select('*')

    if (error) {
      console.error('Error fetching task prerequisites:', error)
      throw error
    }

    return data || []
  },
}
